import { useIntl } from 'react-intl';

/**
 * A failure that names a catalogue key instead of a sentence.
 *
 * Thrown by the modules that talk to the SERVER and worded by the screen that
 * shows it. German lives in the catalogs and nowhere else, and a fetch wrapper
 * has no `intl` to reach for.
 *
 * The sibling of `useValidationMessage`, which does the same for the rule codes
 * the shared Zod schemas emit.
 */
export class MessageError extends Error {
    /**
     * The catalogue key, and its placeholders. Fields rather than constructor
     * parameter properties: `erasableSyntaxOnly` allows no shorthand that emits
     * code.
     */
    readonly messageId: string;
    readonly values: Record<string, string | number>;

    constructor(
        messageId: string,
        values: Record<string, string | number> = {}
    ) {
        // The id doubles as `message`, so one of these landing in a console or
        // an unhandled rejection still says what it was about.
        super(messageId);
        this.name = 'MessageError';
        this.messageId = messageId;
        this.values = values;
    }
}

/**
 * Whatever was thrown, as a sentence a person can read: a {@link MessageError}
 * through the catalogs, anything else — a dropped connection, a bug — as its
 * own text, which is better than a shrug.
 */
export function useErrorMessage() {
    const intl = useIntl();

    return (err: unknown): string => {
        if (err instanceof MessageError) {
            return intl.formatMessage({ id: err.messageId }, err.values);
        }
        return err instanceof Error ? err.message : String(err);
    };
}
